/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */

import { Abwesenheit } from '../entities/Abwesenheit';
import { Zeitstempel } from '../entities/Zeitstempel';
import { Abweichung } from '../../../Abweichung';
import {
  faArrowLeftToBracket,
  faClock,
  faPaperPlane,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@e2n/ui';
import dayjs from 'dayjs';
import { Link, useParams } from 'react-router-dom';
import {
  faArrowLeftToArc,
  faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { useArbeitszeitUpdate } from '@/domain/arbeitszeiten/hooks/useArbeitszeitenUpdate';
import { handleOnCompleted } from '../../../EditSheet/handleOnCompleted';
import * as Sentry from '@sentry/react';
import { toast } from '@/hooks/use-toast';
import { ReactNode, useState } from 'react';
import { useManagerCanEditArbeitszeiten } from '@/domain/arbeitszeiten/hooks/useManagerCanEditArbeitszeit';
import { Pause as PauseEntity } from '@/domain/arbeitszeiten/entities';
import { PausenHoverCard } from '@/domain/arbeitszeiten/components/PausenHoverCard';
import { mapAbwesenheitsGrundToIcon } from '@/domain/arbeitszeiten/shared/util/mapAbwesenheitToIcon';

export function ArbeitszeitCellContentAbwesenheit({
  abwesenheit,
}: {
  abwesenheit: Abwesenheit;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-row items-center gap-4 font-semibold justify-center">
      <FontAwesomeIcon icon={mapAbwesenheitsGrundToIcon(abwesenheit.grund)} />
      <span>
        {t(`entities.abwesenheiten.${abwesenheit.grund.toUpperCase()}`)}
      </span>
    </div>
  );
}

export function ArbeitszeitCellArbeitszeitContent({
  beginn,
  kommen,
  ende,
  gehen,
  pausenDauer,
  pausen,
  id,
}: {
  beginn?: Zeitstempel;
  kommen?: Zeitstempel;
  ende?: Zeitstempel;
  gehen?: Zeitstempel;
  pausenDauer: number;
  pausen: PauseEntity[];
  id: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 pe-4">
      <div className="flex flex-row items-start gap-1 basis-26.25">
        <Beginn beginn={beginn} kommen={kommen} />
      </div>
      <Pause pausen={pausen} pausenDauer={pausenDauer}></Pause>
      <div className="flex flex-row items-start gap-1 basis-26.25">
        <Ende
          id={id}
          ende={ende}
          gehen={gehen}
          beginn={beginn}
          kommen={kommen}
        />
      </div>
    </div>
  );
}

export function ArbeitszeitCellAntragContent({
  beginn,
  kommen,
  ende,
  gehen,
  pausenDauer,
  pausen,
  type,
  id,
}: {
  beginn?: Zeitstempel;
  kommen?: Zeitstempel;
  ende?: Zeitstempel;
  gehen?: Zeitstempel;
  pausenDauer: number;
  pausen: PauseEntity[];
  type: 'Beginn' | 'Ende' | 'BeginnEnde' | undefined;
  id: string;
}) {
  const { year, month, day } = useParams();
  const linkToAntrag = `/arbeitszeiten/uebersicht/${year}/${month}/${day}/sheet/antrag/${id}`;
  let beginnContent = <Beginn beginn={beginn} kommen={kommen} />;
  let endeContent = (
    <Ende id={id} ende={ende} gehen={gehen} beginn={beginn} kommen={kommen} />
  );

  if (type === 'Beginn' || type === 'BeginnEnde') {
    beginnContent = (
      <Link to={linkToAntrag}>
        <div
          className={`flex items-center gap-2 bg-primary-blue-100 p-1 rounded-md`}>
          <span className={`text-primary-blue-600 font-semibold`}>
            {beginn?.zeitpunkt.format('HH:mm')}
          </span>
          <FontAwesomeIcon
            size="xs"
            className={`text-primary-blue-600`}
            icon={faPaperPlane}
          />
        </div>
      </Link>
    );
  }
  if (type === 'Ende' || type === 'BeginnEnde') {
    endeContent = (
      <Link to={linkToAntrag}>
        <div
          className={`flex items-center gap-2 bg-primary-blue-100 p-1 rounded-md`}>
          <span className={`text-primary-blue-600 font-semibold`}>
            {ende?.zeitpunkt.format('HH:mm')}
          </span>
          <FontAwesomeIcon
            size="xs"
            className={`text-primary-blue-600`}
            icon={faPaperPlane}
          />
        </div>
      </Link>
    );
  }

  return (
    <div className="flex items-center justify-between gap-4 pe-4">
      <div className="flex flex-row items-start gap-1 basis-26.25">
        {beginnContent}
      </div>
      <Pause pausen={pausen} pausenDauer={pausenDauer}></Pause>
      <div className="flex flex-row items-start gap-1 basis-26.25">
        {endeContent}
      </div>
    </div>
  );
}

export function Beginn({
  beginn,
  kommen,
}: {
  beginn?: Zeitstempel;
  kommen?: Zeitstempel;
}) {
  const { t } = useTranslation();

  if (beginn && beginn.zeitpunkt) {
    return (
      <div className="flex flex-col gap-1 font-semibold">
        <div className="flex flex-row gap-3 items-center">
          <span>{beginn.zeitpunkt.format('HH:mm')}</span>
          {kommen && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger>
                  <div className="flex gap-1 items-center text-neutrals-500">
                    <FontAwesomeIcon icon={faArrowLeftToBracket} size="sm" />
                    <span className="text-xs text-neutrals-500">
                      {kommen.zeitpunkt.format('HH:mm')}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {t(`domain.arbeitszeiten.uebersicht.kommenGestempelt`)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <Abweichung abweichung={beginn.abweichung} />
      </div>
    );
  }
  return (
    <Badge className="bg-primary-orange-500 hover:bg-primary-orange-500 w-28 flex justify-center font-semibold">
      {t('domain.arbeitszeiten.uebersicht.keineBuchung')}
    </Badge>
  );
}

export function Ende({
  id,
  ende,
  gehen,
  beginn,
  kommen,
}: {
  id: string;
  ende?: Zeitstempel;
  gehen?: Zeitstempel;
  beginn?: Zeitstempel;
  kommen?: Zeitstempel;
}) {
  const { t } = useTranslation();
  const [arbeitszeitUpdate] = useArbeitszeitUpdate();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const canEdit = useManagerCanEditArbeitszeiten();

  async function handleArbeitszeitUpdate() {
    setIsUpdating(true);
    try {
      const result = await arbeitszeitUpdate({
        arbeitszeitId: id,
        arbeitszeitUpdateInput: {
          beginn: beginn?.zeitpunkt?.format('HH:mm'),
          ende: ende?.schicht?.ende?.format('HH:mm'),
        },
      });
      if (result.data) {
        handleOnCompleted(result.data, t);
      } else {
        if (result.error) {
          throw new Error('Unknown Error in ArbeitszeitUpdate');
        }
      }
    } catch (e) {
      toast({
        title: t(
          'domain.arbeitszeiten.sheet.header.arbeitszeitUpdateUnknownError',
        ),
      });
      Sentry.captureException(e);
    } finally {
      setIsUpdating(false);
    }
  }

  if (ende && ende.zeitpunkt) {
    return (
      <div className="flex flex-col gap-1 font-semibold">
        <div className="flex flex-row gap-3 items-center">
          <span>{ende.zeitpunkt.format('HH:mm')}</span>
          {gehen && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger>
                  <div className="flex gap-1 items-center text-neutrals-500">
                    <FontAwesomeIcon icon={faArrowLeftToBracket} size="sm" />
                    <span className="text-xs text-neutrals-500">
                      {gehen.zeitpunkt.format('HH:mm')}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {t(`domain.arbeitszeiten.uebersicht.gehenGestempelt`)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <Abweichung abweichung={ende.abweichung} />
      </div>
    );
  }

  let TooltipTriggerComponent: ReactNode = (
    <FontAwesomeIcon icon={faClock} size="lg" />
  );

  if (
    (beginn &&
      beginn.zeitpunkt &&
      beginn.zeitpunkt.isBefore(dayjs().subtract(24, 'hours'))) ||
    (kommen &&
      kommen.zeitpunkt &&
      kommen.zeitpunkt.isBefore(dayjs().subtract(24, 'hours')))
  ) {
    TooltipTriggerComponent = (
      <Badge className="bg-primary-orange-500 hover:bg-primary-orange-500 font-semibold">
        {t('domain.arbeitszeiten.uebersicht.keineBuchung')}
      </Badge>
    );
  }

  if (isUpdating) {
    TooltipTriggerComponent = (
      <FontAwesomeIcon icon={faSpinner} spin className="text-neutrals-700" />
    );
  }

  if (isTooltipOpen) {
    TooltipTriggerComponent = (
      <div
        className="flex gap-2 items-center font-semibold text-neutrals-700"
        onClick={() => {
          handleArbeitszeitUpdate();
        }}>
        <FontAwesomeIcon
          className="text-primary-blue-500 hover:text-primary-blue-700"
          icon={faArrowLeftToArc}
        />
        {ende?.schicht?.ende?.format('HH:mm')}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip
        open={isTooltipOpen}
        onOpenChange={(open) => {
          if (ende?.schicht?.ende && canEdit) {
            setIsTooltipOpen(open);
          }
        }}
        delayDuration={0}>
        <TooltipTrigger
          className="w-28"
          onPointerDown={(event) => event.preventDefault()}>
          {TooltipTriggerComponent}
        </TooltipTrigger>
        <TooltipContent
          onPointerDownOutside={(event) => {
            event.preventDefault();
          }}>
          <span className="text-sm">
            {t(
              'domain.arbeitszeiten.dienstplanuebernahme.tooltip.endeUebernehmen',
            )}
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function Pause({
  pausenDauer,
  pausen,
}: {
  pausenDauer?: number;
  pausen: PauseEntity[];
}) {
  const { t } = useTranslation();

  if (!pausenDauer || pausenDauer === 0) {
    return <div className="h-px bg-neutrals-500 grow" />;
  }
  return (
    <>
      <div className="h-px bg-neutrals-500 grow" />
      <PausenHoverCard pausen={pausen}>
        <span className="text-xs text-neutrals-700 font-semibold">
          {pausenDauer} {t('general.date.minutes')}
        </span>
      </PausenHoverCard>
      <div className="h-px bg-neutrals-500 grow" />
    </>
  );
}
